import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import AddEmployeeToLearningTrackModal from "../../components/Modals/AddEmployeeToLearningTrackModal";
import { useGetAdminLearningTrackByIdQuery } from "../../services/organization/adminLearningTrack";
import Courses from "../../components/LearningTracks/Admin/Details/Tabs/Courses";
import Details from "../../components/LearningTracks/Admin/Details/Tabs/Details";
import Employees from "../../components/LearningTracks/Admin/Details/Tabs/Employees";
import Quizzes from "../../components/LearningTracks/Admin/Details/Tabs/Quizzes";
import TabsNav from "../../components/tabs/Tabs";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useTranslation } from "react-i18next";
import { useUpdateLearningTrackMutation } from "../../services/organization/adminLearningTrack";
import NotFoundPage from "../404";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";

const TABS = {
  DETAILS: 1,
  COURSES: 2,
  QUIZZES: 3,
  EMPLOYEES: 4,
};

export default function AdminLearningPathDetails() {
  const { learningPathId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [currentTab, setCurrentTab] = useState(1);
  const [isOpen, setIsOpen] = useState(true);
  const { data: learningTrack, isLoading: isLearningTrackLoading } =
    useGetAdminLearningTrackByIdQuery(learningPathId);

  const [updateLearningTrack] = useUpdateLearningTrackMutation();
  let navigate = useNavigate();

  const { t } = useTranslation("learningPaths", "learningPaths");

  if (isLearningTrackLoading) return <LoadingIndicator />;

  if (!learningTrack) {
    return <NotFoundPage />;
  }

  return (
    <div className="admin-learning-path-details">
      <AddEmployeeToLearningTrackModal
        id="add-employee-learning-path"
        learningTrack={learningTrack.data}
        isOpened={showModal}
        onClose={() => setShowModal(false)}
      />

      <ConfirmationModal
        confirmationMessage={t("learningPaths:confirmLearningTrackDelete")}
        handleConfirmation={async () => {
          await updateLearningTrack({
            learningTrackId: learningPathId,
            body: {
              archived: true,
            },
          });
          setIsOpen(false);
          navigate("/admin/learning_paths");
        }}
        isOpened={isOpen}
        onClose={() => setIsOpen((status) => !status)}
      >
        <p className="text-danger">- {t("learningPaths:actionIrreversible")}</p>
        <p className="text-danger">- {t("learningPaths:willNotAppear")}</p>
        <p className="text-danger">- {t("learningPaths:cannotAccessTrack")}</p>
      </ConfirmationModal>

      <div className="sticky-header header-table no-padding-b admin-learning-path-details__header">
        <div className="admin-learning-path-details__toolbar d-flex align-center flex-wrap">
          <div className="left-side admin-learning-path-details__title">
            <h1 className="title-page mt-1">{learningTrack.data.title}</h1>
          </div>

          {!learningTrack?.data?.archived && (
            <div className="ms-auto right-side admin-learning-path-details__actions">
              <ul className="list-filter">
                <li>
                  <button
                    type="button"
                    className="btn btn-main-color font-15"
                    data-bs-toggle="modal"
                    data-bs-target="#add-employee-learning-path"
                    onClick={() => setShowModal(true)}
                  >
                    <span className="fas fa-share me-2"></span>
                    {t("learningPaths:inviteEmployee")}
                  </button>
                </li>
                <li>
                  <Link to={`/admin/learning_paths/${learningPathId}/edit`}>
                    <button
                      type="button"
                      className="btn btn-grey-color font-15"
                    >
                      <span className="fas fa-edit fa-lg fa-fw me-2"></span>
                      {t("learningPaths:EditPath")}
                    </button>
                  </Link>
                </li>
                <li>
                  <div className="dots-tools">
                    <div className="dropdown tools">
                      <button
                        type="button"
                        className="btn btn-grey-color font-15 h-100 admin-learning-path-details__menu-btn"
                        id="admin-lp-details-menu"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="true"
                        aria-expanded="false"
                        aria-label={t("learningPaths:deleteTrack")}
                      >
                        <span className="fas fa-ellipsis-h fa-fw font_14 font-color-grey"></span>
                      </button>
                      <ul
                        className="dropdown-menu dropdown-menu-end"
                        aria-labelledby="admin-lp-details-menu"
                      >
                        <li>
                          <button
                            type="button"
                            className="dropdown-item bold mb-0 font_12 font-extrabold"
                            data-bs-toggle="modal"
                            data-bs-target="#confirmation-modal"
                          >
                            {t("learningPaths:deleteTrack")}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          )}
        </div>

        <TabsNav
          activeIndex={currentTab - 1}
          tabsConfig={[
            {
              name: "home",
              label: t("learningPaths:pathInfo"),
              mobileLabel: t("learningPaths:pathInfoShort"),
              clickHandler: () => setCurrentTab(TABS.DETAILS),
            },

            {
              name: "profile",
              label: t("learningPaths:pathCourse"),
              clickHandler: () => setCurrentTab(TABS.COURSES),
            },
            {
              name: "quizzes",
              label: t("learningPaths:QuestionsAndExams"),
              mobileLabel: t("learningPaths:QuestionsAndExamsShort"),
              clickHandler: () => setCurrentTab(TABS.QUIZZES),
            },
            {
              name: "employees",
              label: t("learningPaths:enrolledEmployees"),
              mobileLabel: t("learningPaths:enrolledEmployeesShort"),
              clickHandler: () => setCurrentTab(TABS.EMPLOYEES),
            },
          ]}
        />
      </div>

      <div
        className="tab-content admin-learning-path-details__body"
        id="nav-tabContent"
      >
        {currentTab === TABS.DETAILS && (
          <Details learningTrack={learningTrack.data} />
        )}
        {currentTab === TABS.COURSES && (
          <Courses learningTrack={learningTrack.data} />
        )}
        {currentTab === TABS.QUIZZES && (
          <Quizzes learningTrack={learningTrack.data} />
        )}
        {currentTab === TABS.EMPLOYEES && (
          <Employees learningTrack={learningTrack.data} />
        )}
      </div>
    </div>
  );
}
